import React, { useState, useEffect, useCallback, useMemo } from 'react';
import GroupEditorModal from '../groups/GroupEditorModal';
import { useNavigate } from 'react-router-dom';
import { Users, User, Plus, Search, Filter, ChevronDown, ChevronUp, X, RefreshCw, Edit, Trash2, UserPlus, Eye, MoreHorizontal } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { UpsellModal } from '../billing/Upsell';
import { FeaturedLayout } from '../layouts';
import GroupRosterModal from '../groups/GroupRosterModal';

import StaffSelector from './StaffSelector';
import { DEFAULT_SPORT, normalizeSportValue } from '../../constants/sports';
import { useDeportes } from '../../hooks/useDeportes';
import { opcionesDeporte, deportePorDefecto, SIN_DEPORTES_MSG } from '../../lib/deportesOrg';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Badge,
  CategoryMultiSelect
} from '../ui';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  getAllPlayersForAssignment,
  getAllGroupsWithCoaches,
  getCategorias,
  createGroup,
  updateGroup,
  getAllCoaches,
  getGroupStaff,
  assignStaffToGroup,
  removeStaffFromGroup,
  updateStaffRole,
  getOrgDeportes
} from '../../lib/supabase';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { RESTRICTION_OPTIONS, RESTRICTION_HINTS } from '../../lib/categorias';

// Constantes para tipos de grupo (debe coincidir con Google Sheets)
const TIPOS_DE_GRUPO = [
  { value: 'Grupo base', label: 'Grupo base' },
  { value: 'Torneo', label: 'Torneo' },  { value: 'Actividad', label: 'Actividad' },
  { value: 'Otro', label: 'Otro' }
];

const SPORT_BADGE_STYLES = {
  flag: 'bg-purple-100 text-purple-700',
  tackle: 'bg-blue-100 text-blue-700',
  futbol: 'bg-amber-100 text-amber-700'
};

const EMPTY_GROUP_FORM = {
  nombre_del_grupo: '',
  categoria_base: '',
  rama: 'Varonil',
  deporte: '',
  tipo_de_grupo: 'Grupo base',
  tipo_restriccion: 'recomendada',
  estatus: 'Activo',
  descripcion: '',
  coach_id: ''
};

const createEmptyGroupForm = () => ({ ...EMPTY_GROUP_FORM });

const sanitizeCategoryIds = (ids = []) =>
  Array.from(new Set(ids.filter((value) => Boolean(value))));

const GroupManagement = () => {
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const { can } = useEntitlements();
  const { org } = useOrganization();
  const { label: deporteLabel } = useDeportes();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();
  const [players, setPlayers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgDeportes, setOrgDeportes] = useState([]);
  const [deportesCargados, setDeportesCargados] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Cargar deportes activos de la org
  useEffect(() => {
    if (!org?.id) return;
    getOrgDeportes(org.id).then(({ data }) => {
      if (data) setOrgDeportes(data);
      setDeportesCargados(true);
    });
  }, [org?.id]);
  
  // Group creation
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [limitUpsell, setLimitUpsell] = useState(false);
  const [newGroup, setNewGroup] = useState(() => createEmptyGroupForm());

  // Multiple staff assignments for new group
  const [newGroupStaff, setNewGroupStaff] = useState([]);
  const [newGroupCategories, setNewGroupCategories] = useState([]);

  // Group editing
  // Group editing
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);





  // Group listing filters
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [groupStatusFilter, setGroupStatusFilter] = useState('activos');
  const [groupSportFilter, setGroupSportFilter] = useState('todos');
  const [showGroupFilters, setShowGroupFilters] = useState(false);
  const hasActiveGroupFilters = groupStatusFilter !== 'activos' || groupSportFilter !== 'todos';
  const [rosterGroup, setRosterGroup] = useState(null);

  const getPrimaryCategoryName = useCallback(
    (selectedIds) => {
      if (!selectedIds?.length) return '';
      const firstId = selectedIds[0];
      const found = categories.find((cat) => cat.id === firstId);
      return found?.nombre || '';
    },
    [categories]
  );

  const resetNewGroupForm = useCallback(() => {
    setNewGroup(createEmptyGroupForm());
    setNewGroupStaff([]);
    setNewGroupCategories([]);
  }, []);

  const closeCreateGroup = useCallback(() => {
    resetNewGroupForm();
    setShowCreateGroup(false);
  }, [resetNewGroupForm]);

  const createGroupGuard = useUnsavedGuard({ onClose: closeCreateGroup });








  // Redirect if not admin
  useEffect(() => {
    if (!loading && !isAdmin) {
      window.location.href = '/groups';
    }
  }, [isAdmin, loading]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [playersData, groupsData, coachesData, categoriesData] = await Promise.all([
        getAllPlayersForAssignment(),
        getAllGroupsWithCoaches(null, { includeArchived: true }),
        getAllCoaches(),
        getCategorias({ includeInactive: true })
      ]);

      if (playersData.error) throw playersData.error;
      if (groupsData.error) throw groupsData.error;
      if (coachesData.error) throw coachesData.error;
      if (categoriesData.error) throw categoriesData.error;

      const mappedGroups = (groupsData.data || []).map((group) => {
        const categoriasRelacionadas =
          group.grupos_categorias?.map((rel) => ({
            id: rel.categoria_id,
            ...(rel.categorias || {})
          })) || [];
        const categoriasFiltradas = categoriasRelacionadas.filter(
          (cat) => cat.id && cat.activo !== false
        );
        return {
          ...group,
          categorias: categoriasFiltradas,
          categoria_ids: categoriasFiltradas.map((cat) => cat.id)
        };
      });

      setPlayers(playersData.data || []);
      setGroups(mappedGroups);
      setCoaches(coachesData.data || []);
      setCategories(categoriesData.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      await alert('Error', 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [alert]);

  // Legacy: handleSyncAndReload (Google Sheets sync) eliminado en Fase 8.
  // Si todavia esta referenciado en el JSX como handleSyncAndReload, recargar
  // simplemente ahora usa loadData directamente.

  const filteredGroupList = useMemo(() => {
    const term = groupSearchTerm.trim().toLowerCase();
    return groups.filter(group => {
      const estatusLower = (group.estatus || '').toString().trim().toLowerCase();
      if (groupStatusFilter === 'activos') {
        if (estatusLower !== 'activo') return false;
      } else if (groupStatusFilter === 'inactivos') {
        if (estatusLower !== 'inactivo') return false;
      } else if (groupStatusFilter === 'archivados') {
        if (estatusLower !== 'cerrado' && estatusLower !== 'archivado') return false;
      }

      const sportValue = normalizeSportValue(group.deporte || DEFAULT_SPORT);
      if (groupSportFilter !== 'todos' && sportValue !== normalizeSportValue(groupSportFilter)) {
        return false;
      }

      if (!term) return true;

      const coachName =
        (group.staff_grupos?.find(rel => rel.activo !== false && rel.staff)?.staff?.nombre_completo || '').toLowerCase();
      const sportLabel = deporteLabel(group.deporte).toLowerCase();
      const categoryNames = (group.categorias || [])
        .map((cat) => (cat?.nombre || '').toLowerCase())
        .join(' ');

      return (
        (group.nombre_del_grupo || '').toLowerCase().includes(term) ||
        (group.rama || '').toLowerCase().includes(term) ||
        (group.tipo_de_grupo || '').toLowerCase().includes(term) ||
        coachName.includes(term) ||
        sportLabel.includes(term) ||
        categoryNames.includes(term)
      );
    });
  }, [groupSearchTerm, groups, groupStatusFilter, groupSportFilter, deporteLabel]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, loadData]);

  const handleCreateGroup = async () => {
    if (!newGroup.nombre_del_grupo.trim()) {
      await alert('Error', 'El nombre del grupo es obligatorio');
      return;
    }

    if (!newGroup.deporte) {
      await alert('Error', orgDeportes.length === 0 ? SIN_DEPORTES_MSG : 'Elige el deporte del grupo');
      return;
    }

    const categoryIds = sanitizeCategoryIds(newGroupCategories);
    if (categoryIds.length === 0) {
      await alert('Error', 'Selecciona al menos una categoría para el grupo');
      return;
    }

    try {
      setSyncing(true);

      const basePayload = { ...newGroup };
      const groupDataToSend = {
        ...basePayload,
        categoria_ids: categoryIds,
        staff_assignments: newGroupStaff.map(assignment => ({
          staff_id: assignment.staff_id,
          rol: assignment.rol
        }))
      };

      const primaryCategoryName = getPrimaryCategoryName(categoryIds);
      groupDataToSend.categoria_base = primaryCategoryName || '';

      if (!groupDataToSend.coach_id) {
        delete groupDataToSend.coach_id;
      }

      const { error, categoriasError } = await createGroup(groupDataToSend);
      if (error) throw error;

      await loadData();
      createGroupGuard.resetDirty();
      resetNewGroupForm();
      setShowCreateGroup(false);
      if (categoriasError) {
        await alert('Grupo creado sin categorías', `El grupo se creó, pero sus categorías no se guardaron: ${categoriasError.message}`);
      } else {
        await alert('Grupo creado', 'El grupo se creó correctamente');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      await alert('Error', error.message || 'Error creando grupo');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenCreateGroup = () => {
    if (!can('groups')) { setLimitUpsell(true); return; }
    setNewGroup(prev => (prev.deporte ? prev : { ...prev, deporte: deportePorDefecto(orgDeportes) }));
    setShowCreateGroup(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowEditGroup(true);
  };































































  const doArchiveGroup = async (group, closeEditor = false) => {
    if (!group) return;
    if (!await confirm('Desactivar grupo', `"${group.nombre_del_grupo}" se marcará como inactivo. Su historial permanecerá disponible.`, { destructive: true })) return;

    try {
      setSyncing(true);
      const archivePayload = { estatus: 'Inactivo' };
      if (Object.prototype.hasOwnProperty.call(group, 'activo')) archivePayload.activo = false;
      if (Object.prototype.hasOwnProperty.call(group, 'deleted_at')) archivePayload.deleted_at = new Date().toISOString();
      if (Object.prototype.hasOwnProperty.call(group, 'fecha_eliminacion')) archivePayload.fecha_eliminacion = new Date().toISOString();

      const { error } = await updateGroup(group.id, archivePayload);
      if (error) throw error;

      await loadData();
      if (closeEditor) { setShowEditGroup(false); setEditingGroup(null); }
    } catch (error) {
      console.error('Error disabling group:', error);
      await alert('Error', 'No se pudo desactivar el grupo.');
    } finally {
      setSyncing(false);
    }
  };

  const handleArchiveGroup = () => doArchiveGroup(editingGroup, true);
  const handleArchiveGroupFromCard = (group) => doArchiveGroup(group, false);

  const getStatusDotClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'activo': return 'bg-green-500';
      case 'inactivo': return 'bg-yellow-500';
      case 'archivado': return 'bg-muted-foreground';
      default: return 'bg-muted-foreground';
    }
  };






  return (
    <FeaturedLayout
      title="Gestión de Grupos"
      subtitle="Administra grupos y asigna jugadores"
      refreshAction={loadData}
    >
      <div>
        <div className="space-y-4">
          {/* Header */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-nowrap">
              <div className="flex-1 min-w-0 relative">
                <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  placeholder="Buscar grupo, rama o coach..."
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
                  <span className="font-medium text-foreground">{filteredGroupList.length}</span>{' '}
                  {filteredGroupList.length === 1 ? 'grupo' : 'grupos'}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setShowGroupFilters((prev) => !prev)}
                  className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
                >
                  <Filter className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Filtros</span>
                  {showGroupFilters
                    ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" />
                    : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
                  {hasActiveGroupFilters && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                  )}
                </Button>
                <Button onClick={handleOpenCreateGroup} className="h-10" aria-label="Crear grupo" title="Crear grupo">
                  <Plus className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Crear grupo</span>
                </Button>
              </div>
            </div>
            {showGroupFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Estatus</Label>
                  <Select value={groupStatusFilter} onValueChange={setGroupStatusFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="activos">Solo activos</SelectItem>
                      <SelectItem value="inactivos">Inactivos</SelectItem>
                      <SelectItem value="archivados">Archivados</SelectItem>
                      <SelectItem value="todos">Todos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Deporte</Label>
                  <Select value={groupSportFilter} onValueChange={setGroupSportFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Todos los deportes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los deportes</SelectItem>
                      {opcionesDeporte(orgDeportes).map((sport) => (
                        <SelectItem key={sport.value} value={sport.value}>
                          {sport.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    className="text-gray-600 hover:text-gray-900 px-0"
                    onClick={() => {
                      setGroupStatusFilter('activos');
                      setGroupSportFilter('todos');
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Groups Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground text-sm">Cargando grupos...</p>
              </div>
            </div>
          ) : filteredGroupList.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground mb-2">
                  {groups.length === 0 ? 'Todavía no hay grupos' : 'No se encontraron grupos'}
                </p>
                {groups.length === 0 ? (
                  <Button size="sm" className="mt-2" onClick={handleOpenCreateGroup}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crear el primero
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ajusta la búsqueda o quita los filtros para ver otros grupos.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGroupList.map((group) => {
                const activeAssignments = (group.staff_grupos || []).filter(rel => rel.activo !== false && rel.staff);
                const currentCoach = activeAssignments.length > 0 ? activeAssignments[0].staff : null;
                const sportValue = normalizeSportValue(group.deporte || DEFAULT_SPORT);
                const sportLabel = deporteLabel(group.deporte);
                const sportBadgeClass = SPORT_BADGE_STYLES[sportValue] || 'bg-gray-100 text-gray-700';
                
                const playersCount = (group.jugadores_grupos || []).filter(jg =>
                  jg.estatus === 'Activo' && jg.activo !== false && !jg.deleted
                ).length;
                const meta = [group.rama, group.tipo_de_grupo].filter(Boolean).join(' · ');

                return (
                  <Card key={group.id} className="flex flex-col h-full transition-shadow hover:shadow-md">
                    <CardContent className="p-4 flex flex-col gap-2 flex-grow">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4
                            className="text-base font-semibold leading-tight truncate cursor-pointer hover:text-primary"
                            onClick={() => navigate(`/groups/${group.id}`)}
                          >
                            {group.nombre_del_grupo}
                          </h4>
                          {meta && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{meta}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="shrink-0 gap-1.5 font-normal text-[11px] px-2 py-0.5">
                          <span className={`h-1.5 w-1.5 rounded-full ${getStatusDotClass(group.estatus)}`} />
                          {group.estatus}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${sportBadgeClass}`}>
                          {sportLabel}
                        </span>
                        {group.categorias && group.categorias.length > 0 ? (
                          group.categorias.map((cat) => (
                            <Badge key={`${group.id}-${cat.id}`} variant="secondary" className="text-[11px] font-normal">
                              {cat.nombre || 'categoría'}
                            </Badge>
                          ))
                        ) : (
                          group.categoria_base && (
                            <Badge variant="outline" className="text-[11px] font-normal">
                              {group.categoria_base}
                            </Badge>
                          )
                        )}
                      </div>

                      {group.descripcion && (
                        <p className="text-xs text-muted-foreground truncate">{group.descripcion}</p>
                      )}

                      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-muted-foreground mt-auto pt-1">
                        <span className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 shrink-0" />
                          {playersCount} jugadores
                        </span>
                        {currentCoach ? (
                          <span className="flex items-center gap-1.5 min-w-0">
                            <User className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{currentCoach.nombre_completo}</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-destructive">
                            <User className="h-3.5 w-3.5 shrink-0" />
                            Sin coach
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          className="flex-1 h-8"
                          onClick={() => setRosterGroup(group)}
                        >
                          <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                          Jugadores
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-8"
                          onClick={() => handleEditGroup(group)}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1.5" />
                          Editar
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/groups/${group.id}`)}>
                              <Eye className="h-4 w-4" />
                              Ver detalle
                            </DropdownMenuItem>
                            {group.estatus?.toLowerCase() !== 'inactivo' && (
                              <DropdownMenuItem
                                onClick={() => handleArchiveGroupFromCard(group)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Desactivar grupo
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
        {/* Create Group Modal */}
        <Dialog open={showCreateGroup} onOpenChange={createGroupGuard.onOpenChange}>
          <DialogContent {...createGroupGuard.dirtyProps} className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Grupo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Grupo *</Label>
                <Input
                  value={newGroup.nombre_del_grupo}
                  onChange={(e) => setNewGroup(prev => ({ ...prev, nombre_del_grupo: e.target.value }))}
                  placeholder="Ej: Menores 2024"
                />
              </div>

              <CategoryMultiSelect
                label="categorías permitidas"
                selectedIds={newGroupCategories}
                onChange={(ids) => { createGroupGuard.markDirty(); setNewGroupCategories(ids); }}
                options={categories.filter((cat) => cat.activo !== false)}
                sport={newGroup.deporte}
                disabled={loading}
                emptyMessage="No hay categorías disponibles. Configúralas en Administración > categorías."
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rama</Label>
                  <Select value={newGroup.rama} onValueChange={(v) => { createGroupGuard.markDirty(); setNewGroup(prev => ({ ...prev, rama: v })); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Varonil">Varonil</SelectItem>
                      <SelectItem value="Femenil">Femenil</SelectItem>
                      <SelectItem value="Mixto">Mixto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Deporte</Label>
                  <Select
                    value={newGroup.deporte}
                    disabled={deportesCargados && orgDeportes.length === 0}
                    onValueChange={(v) => {
                      createGroupGuard.markDirty();
                      setNewGroup(prev => ({ ...prev, deporte: v }));
                      // Un grupo es de un solo deporte: las categorias ya marcadas del otro se sueltan.
                      setNewGroupCategories(prev => prev.filter((id) =>
                        normalizeSportValue(categories.find((cat) => cat.id === id)?.deporte) === normalizeSportValue(v)
                      ));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={!deportesCargados ? 'Cargando...' : orgDeportes.length === 0 ? 'Sin deportes habilitados' : 'Seleccionar'} />
                    </SelectTrigger>
                    <SelectContent>
                      {opcionesDeporte(orgDeportes).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {deportesCargados && orgDeportes.length === 0 && (
                    <p className="text-xs text-muted-foreground">{SIN_DEPORTES_MSG}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Grupo</Label>
                  <Select value={newGroup.tipo_de_grupo} onValueChange={(v) => { createGroupGuard.markDirty(); setNewGroup(prev => ({ ...prev, tipo_de_grupo: v })); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS_DE_GRUPO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>¿La categoría restringe?</Label>
                  <Select value={newGroup.tipo_restriccion} onValueChange={(v) => { createGroupGuard.markDirty(); setNewGroup(prev => ({ ...prev, tipo_restriccion: v })); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RESTRICTION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{RESTRICTION_HINTS[newGroup.tipo_restriccion]}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estatus</Label>
                  <Select value={newGroup.estatus} onValueChange={(v) => { createGroupGuard.markDirty(); setNewGroup(prev => ({ ...prev, estatus: v })); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Activo">Activo</SelectItem>
                      <SelectItem value="Inactivo">Inactivo</SelectItem>
                      <SelectItem value="En preparación">En preparación</SelectItem>
                      <SelectItem value="Cerrado">Cerrado</SelectItem>
                      <SelectItem value="Archivado">Archivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={newGroup.descripcion}
                  onChange={(e) => setNewGroup(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción del grupo..."
                  rows={3}
                />
              </div>

              <StaffSelector
                selectedStaff={newGroupStaff}
                onChange={(staff) => { createGroupGuard.markDirty(); setNewGroupStaff(staff); }}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => createGroupGuard.onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleCreateGroup} disabled={syncing || !newGroup.nombre_del_grupo.trim()}>
                {syncing ? 'Creando...' : 'Crear Grupo'}
              </Button>
            </DialogFooter>
            {createGroupGuard.DiscardDialog}
          </DialogContent>
        </Dialog>


        <GroupEditorModal
          open={showEditGroup && Boolean(editingGroup)}
          group={editingGroup}
          onClose={() => {
            setShowEditGroup(false);
            setEditingGroup(null);
          }}
          onSaved={async () => {
            await loadData();
            setShowEditGroup(false);
            setEditingGroup(null);
          }}
          categories={categories}
          showError={(t, m) => alert(t, m)}
          showSuccess={(t, m) => alert(t, m)}
        />




















































































































































        <ConfirmDialog />

        <UpsellModal
          open={limitUpsell}
          onOpenChange={setLimitUpsell}
          feature="payments_module"
          title="Límite de grupos alcanzado"
          message="Alcanzaste el límite de grupos de tu plan. Mejora tu plan para agregar más."
        />

        <GroupRosterModal
          group={rosterGroup}
          isOpen={Boolean(rosterGroup)}
          initialTab="assign"
          onClose={() => setRosterGroup(null)}
          onSuccess={() => {
            setRosterGroup(null);
            loadData();
          }}
        />
      </div>
    </FeaturedLayout>
  );
};

export default GroupManagement;







